import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, Play, Pause, Stop, WaveTriangle, Warning } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { useAudio } from "../context/AudioContext";
import { FounderShell, MetricCard } from "../components/founder";

const STATES = [
  "landing", "dashboard", "expedition_map", "expedition_exploration", "rising_danger",
  "skill_challenge", "recovery_camp", "success", "critical_success", "failure",
  "hall_of_renown", "offline", "reconnecting",
];
const EXPEDITIONS = ["frozen_pass", "temple_echoes", "smugglers_coast", "gilded_meridian"];

const Panel = ({ title, children, testid, subtitle }) => (
  <div className="ff-panel rounded-md p-5" data-testid={testid}>
    <h2 className="text-lg font-semibold text-ff-gold">{title}</h2>
    {subtitle && <p className="mt-0.5 text-xs text-ff-muted">{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between border-b border-ff-brass/10 py-1.5 text-sm">
    <span className="text-ff-muted">{label}</span><span className="text-ff-parchment">{children}</span>
  </div>
);

const Btn = ({ onClick, children, testid, tone = "brass", disabled }) => {
  const tones = {
    brass: "border-ff-brass/30 text-ff-brass hover:border-ff-gold hover:text-ff-gold",
    gold: "bg-ff-gold text-ff-bg font-semibold hover:bg-ff-brass border-transparent",
    red: "border-red-500/50 text-red-300 hover:bg-red-500/10",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors disabled:opacity-40 ${tones[tone]}`}>{children}</button>
  );
};

export default function FounderAudioStatus() {
  const audio = useAudio();
  const [agg, setAgg] = useState(null);
  const [state, setState] = useState("dashboard");
  const [exp, setExp] = useState("frozen_pass");
  const [crewSel, setCrewSel] = useState("elara");
  const [roleSel, setRoleSel] = useState("base");

  const loadAgg = useCallback(() => {
    api.get("/founder/audio-status").then((r) => setAgg(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { loadAgg(); audio?.refreshPreview(); }, [loadAgg]); // eslint-disable-line

  const d = audio?.diag || {};
  const pv = audio?.preview || {};
  const g = audio?.gp || {};
  const b = audio?.bv || {};
  const clk = b.clock || {};

  const call = (fn, ok) => async () => {
    try { await fn(); if (ok) toast.success(ok); loadAgg(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doStart = async () => {
    const r = await audio.startPreview();
    if (r.ok) toast.success("Audio preview started"); else toast.error(`Cannot start (${r.reason})`);
  };
  const doAudition = async () => {
    const r = await audio.audition(state, state === "expedition_exploration" ? exp : null);
    if (r.played) toast.success(`Playing ${state}`); else toast.error(`Not audible (${r.reason})`);
  };
  const auditionCrew = async (role) => {
    const r = await audio.auditionCrew(crewSel, role || roleSel, {});
    if (r && r.played) toast.success(`Crew motif: ${crewSel} · ${role || roleSel}`);
    else toast.error(`Not audible (${(r && r.reason) || "unknown"})`);
  };
  const simulateCrewEvents = (count) => {
    const crew = (agg?.crew_motifs?.coverage?.canonical_crew) || ["elara", "tomas", "mira", "jonas", "nia", "silas"];
    const evts = ["crew_contribution_confirmed", "crew_central_to_outcome", "crew_recovered", "crew_loyalty_increased"];
    crew.slice(0, count).forEach((cid, i) => audio.gameplay({
      type: evts[i % evts.length], crew_id: cid, session_id: `sim-${Date.now()}`,
      confirmed: true, event_version: "crew-audio-v1",
    }));
    toast.success(`Dispatched ${count} crew events (max 3 audible)`);
    setTimeout(loadAgg, 300);
  };
  const cm = agg?.crew_motifs || {};
  const cov = cm.coverage || {};

  return (
    <FounderShell>
      <div className="space-y-4" data-testid="founder-audio-page">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Expedition Pulse — Audio</h1>
          <Btn onClick={() => { loadAgg(); audio.refreshPreview(); }} testid="audio-refresh"><ArrowsClockwise size={14} /> Refresh</Btn>
        </div>

        <Panel title="Internal Preview" subtitle="Backend-authoritative, expiring entitlement. Founders only." testid="audio-preview-control">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Row label="Internal preview enabled">{String(pv.internal_preview_enabled)}</Row>
              <Row label="Eligible">{String(pv.eligible)}</Row>
              <Row label="Authorized"><span data-testid="audio-authorized" className={pv.authorized ? "text-emerald-300" : "text-ff-muted"}>{String(pv.authorized)}</span></Row>
              <Row label="Reason">{pv.reason || "—"}</Row>
              <Row label="Emergency silence"><span className={pv.emergency_silence ? "text-red-300" : "text-ff-muted"}>{String(pv.emergency_silence)}</span></Row>
              <Row label="Entitlement expires">{pv.expires_at ? String(pv.expires_at).slice(0, 19).replace("T", " ") : "—"}</Row>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                <Btn onClick={call(audio.enablePreview, "Preview enabled")} testid="audio-enable-preview" tone="gold">Enable preview</Btn>
                <Btn onClick={call(audio.disablePreview, "Preview disabled")} testid="audio-disable-preview">Disable</Btn>
              </div>
              <div className="flex flex-wrap gap-2">
                <Btn onClick={doStart} testid="audio-start-preview" tone="gold" disabled={!pv.authorized}><Play size={14} weight="fill" /> Start Audio Preview</Btn>
                <Btn onClick={audio.stopPreview} testid="audio-stop-preview"><Stop size={14} weight="fill" /> Stop</Btn>
              </div>
              <div className="flex flex-wrap gap-2">
                <Btn onClick={call(() => audio.setEmergency(!pv.emergency_silence, "founder toggle"), "Updated")} testid="audio-emergency-toggle" tone="red"><Warning size={14} /> {pv.emergency_silence ? "Clear emergency silence" : "Emergency silence"}</Btn>
              </div>
            </div>
          </div>
          {!d.userInteracted && <p className="mt-2 text-[11px] text-ff-muted">Autoplay-safe: audio only begins after you press <b>Start Audio Preview</b>.</p>}
        </Panel>

        <Panel title="Internal Audition" subtitle="Preview scoring per state/expedition. Master volume is capped for safety." testid="audio-audition">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-ff-brass">State
              <select value={state} onChange={(e) => setState(e.target.value)} data-testid="audio-state-select" className="mt-1 block rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment">
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-xs text-ff-brass">Expedition
              <select value={exp} onChange={(e) => setExp(e.target.value)} disabled={state !== "expedition_exploration"} data-testid="audio-exp-select" className="mt-1 block rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment disabled:opacity-40">
                {EXPEDITIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <Btn onClick={doAudition} testid="audio-audition-play" tone="gold"><Play size={14} weight="fill" /> Play state</Btn>
            <Btn onClick={audio.pause} testid="audio-pause"><Pause size={14} weight="fill" /> Pause</Btn>
            <Btn onClick={audio.resume} testid="audio-resume"><Play size={14} /> Resume</Btn>
            <Btn onClick={audio.fadeOut} testid="audio-fadeout"><WaveTriangle size={14} /> Fade out</Btn>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Btn onClick={() => audio.audition("success", null)} testid="audio-cue-success">Success cue</Btn>
            <Btn onClick={() => audio.audition("critical_success", null)} testid="audio-cue-crit">Critical success cue</Btn>
            <Btn onClick={() => audio.audition("failure", null)} testid="audio-cue-failure">Failure cue</Btn>
          </div>
          <div className="mt-4">
            <div className="mb-1 flex justify-between text-sm"><span className="text-ff-parchment">Master preview volume (capped)</span><span className="text-ff-muted">{Math.round((d.previewVolume ?? 0.6) * 100)}%</span></div>
            <input type="range" min="0" max="1" step="0.05" value={d.previewVolume ?? 0.6} onChange={(e) => audio.setPreviewVolume(parseFloat(e.target.value))} data-testid="audio-preview-volume" className="w-full cursor-pointer accent-ff-gold" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["calm_mode", "reduced_intensity", "mono_mix"].map((k) => (
              <button key={k} onClick={() => audio.updateSetting({ [k]: !audio.settings?.[k] })} data-testid={`audio-toggle-${k}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${audio.settings?.[k] ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted"}`}>{k.replace(/_/g, " ")}</button>
            ))}
            <button onClick={() => audio.updateSetting({ dynamic_tension_enabled: !audio.settings?.dynamic_tension_enabled })} data-testid="audio-toggle-tension"
              className={`rounded-full border px-3 py-1 text-xs ${audio.settings?.dynamic_tension_enabled ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted"}`}>dynamic tension</button>
          </div>
        </Panel>

        <div className="grid gap-4 md:grid-cols-2">
          <Panel title="Live Diagnostics" testid="audio-diagnostics">
            <Row label="AudioContext"><span data-testid="diag-context">{d.contextState}</span></Row>
            <Row label="Sound produced">{String(d.soundProduced)}</Row>
            <Row label="Assets bound">{String(d.assetsBound)} ({d.manifestTracks} tracks)</Row>
            <Row label="Current state">{d.currentState || "—"}</Row>
            <Row label="Expedition">{d.currentExpedition || "—"}</Row>
            <Row label="BPM">{d.currentBpm || "—"}</Row>
            <Row label="Intensity">{d.currentIntensity ? d.currentIntensity.join("–") : "—"}</Row>
            <Row label="Active stems"><span data-testid="diag-stems">{(d.activeStems || []).length ? d.activeStems.join(", ") : "none"}</span></Row>
            <Row label="Load failures">{d.loadFailures}</Row>
            <Row label="Transition failures">{d.transitionFailures}</Row>
            <Row label="Awaiting restore">{String(d.awaitingRestore)}</Row>
            <div className="mt-3 flex gap-2">
              <Btn onClick={() => { audio.clearCache(); toast.success("Local audio cache cleared"); }} testid="audio-clear-cache">Clear local cache</Btn>
              <Btn onClick={call(audio.resetSettings, "Audio settings reset")} testid="audio-reset-settings">Reset audio settings</Btn>
            </div>
          </Panel>

          <Panel title="Aggregate Telemetry" subtitle="Aggregate-only. No per-player audio behaviour or PII." testid="audio-aggregate">
            {!agg ? <p className="text-sm text-ff-muted">Loading…</p> : (
              <>
                <div className="mb-2 grid grid-cols-2 gap-2" data-testid="audio-aggregate-note">
                  <MetricCard label="Profiles" metric={{ value: agg.profiles }} testid="audio-profiles" />
                  <MetricCard label="Active Entitlements" metric={{ value: agg.preview?.entitlements_active ?? 0 }} testid="audio-active-ent" />
                </div>
                <Row label="Manifest version">{agg.manifest?.manifest_version}</Row>
                <Row label="Valid tracks">{agg.manifest?.valid_tracks}</Row>
                <Row label="Rejected tracks">{agg.manifest?.rejected_tracks}</Row>
                <Row label="License">{agg.manifest?.license_id}</Row>
                <div className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="audio-flags">
                  {Object.entries(agg.flags || {}).map(([k, v]) => (
                    <span key={k} className={`rounded-full border px-2 py-0.5 ${v ? "border-emerald-500/40 text-emerald-300" : "border-ff-brass/30 text-ff-muted"}`}>{k}: {String(v)}</span>
                  ))}
                </div>
              </>
            )}
          </Panel>
        </div>

        <Panel title="Gameplay Wiring" subtitle="Live server-confirmed gameplay → audio. Route music is only a fallback." testid="audio-gameplay-wiring">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Row label="Current route"><span data-testid="gp-route">{g.currentRoute || "—"}</span></Row>
              <Row label="Session active"><span data-testid="gp-session">{String(g.sessionActive)}</span></Row>
              <Row label="Authoritative state"><span data-testid="gp-auth-state">{g.authoritativeState || "—"}</span></Row>
              <Row label="Fallback state">{g.fallbackState || "—"}</Row>
              <Row label="Danger level"><span data-testid="gp-danger">{g.dangerLevel || "—"}</span></Row>
              <Row label="Danger score"><span data-testid="gp-danger-score">{g.dangerScore ?? 0}</span> ({g.dangerVersion || "—"})</Row>
              <Row label="Prev danger">{g.prevDangerLevel || "—"}</Row>
              <Row label="Danger reasons"><span data-testid="gp-danger-reasons" className="inline-block max-w-[150px] truncate align-bottom">{(g.dangerReasons || []).join(", ") || "—"}</span></Row>
              <Row label="Tension suppressed"><span data-testid="gp-danger-suppressed">{String(!!g.dangerSuppressed)}</span></Row>
              <Row label="Danger fallback used">{String(!!g.fallbackUsed)}</Row>
              <Row label="Expedition theme">{g.expeditionTheme || "—"}</Row>
            </div>
            <div>
              <Row label="Last event">{g.lastEvent?.type || "—"}</Row>
              <Row label="Last server event"><span data-testid="gp-last-server">{g.lastServerEvent?.type || "—"}</span></Row>
              <Row label="Last cue key"><span data-testid="gp-cue-key" className="inline-block max-w-[160px] truncate align-bottom">{g.lastCueKey || "—"}</span></Row>
              <Row label="Dedup records">{g.dedupSize ?? 0}</Row>
              <Row label="Duplicates prevented"><span data-testid="gp-dup">{g.dupPrevented ?? 0}</span></Row>
              <Row label="Active crew motifs">{(g.activeCrewMotifs || []).length}/3</Row>
              <Row label="Suspended (offline)">{String(g.suspended)}</Row>
              <Row label="Transition failures">{g.transitionFailures ?? 0}</Row>
            </div>
          </div>
          {(g.ignored || []).length > 0 && (
            <div className="mt-3" data-testid="gp-ignored">
              <div className="mb-1 text-xs uppercase tracking-widest text-ff-brass">Recent ignored client-only events</div>
              <div className="flex flex-wrap gap-1.5">
                {g.ignored.map((x, i) => <span key={i} className="rounded-full border border-ff-brass/25 px-2 py-0.5 text-[11px] text-ff-muted">{x.type}: {x.reason}</span>)}
              </div>
            </div>
          )}
          {(g.eventLog || []).length > 0 && (
            <div className="mt-3" data-testid="gp-eventlog">
              <div className="mb-1 text-xs uppercase tracking-widest text-ff-brass">Event log (dev, bounded)</div>
              <div className="max-h-32 overflow-auto rounded-sm border border-ff-brass/15 bg-ff-bg/40 p-2 font-mono-ff text-[11px] text-ff-muted">
                {g.eventLog.slice().reverse().map((x, i) => <div key={i}>{x.type}{x.confirmed ? " ✓" : ""}</div>)}
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Crew Motifs" subtitle="Decorative per-crew themes on server-confirmed defining moments. Founder preview only." testid="audio-crew-motifs-panel">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Row label="Motif feature (players)"><span data-testid="crew-flag">{String(d.flags?.PLAYER_MOTIFS_ENABLED)}</span></Row>
              <Row label="Preview authorized">{String(pv.authorized)}</Row>
              <Row label="All 6 crew covered"><span data-testid="crew-covered">{String(cov.all_covered)}</span></Row>
              <Row label="Crew tracks bound">{d.crewTracksBound ?? 0}</Row>
              <Row label="Max simultaneous">{cm.max_simultaneous ?? 3}</Row>
              <Row label="Event version">{cov.event_version || "—"}</Row>
              <Row label="Active crew motifs"><span data-testid="crew-active">{(d.activeCrewMotifs || []).map((e) => `${e.crewId}:${e.role}`).join(", ") || "none"}</span></Row>
              <Row label="Last crew motif">{d.lastCrewMotif ? `${d.lastCrewMotif.crewId}:${d.lastCrewMotif.role}` : "—"}</Row>
            </div>
            <div>
              <div className="mb-2 flex flex-wrap items-end gap-2">
                <label className="text-xs text-ff-brass">Crew
                  <select value={crewSel} onChange={(e) => setCrewSel(e.target.value)} data-testid="crew-select" className="mt-1 block rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment">
                    {(cov.canonical_crew || ["elara", "tomas", "mira", "jonas", "nia", "silas"]).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="text-xs text-ff-brass">Variant
                  <select value={roleSel} onChange={(e) => setRoleSel(e.target.value)} data-testid="crew-role-select" className="mt-1 block rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment">
                    {["base", "calm", "injury", "recovery", "defining"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Btn onClick={() => auditionCrew()} testid="crew-play" tone="gold"><Play size={14} weight="fill" /> Play motif</Btn>
                <Btn onClick={() => auditionCrew("injury")} testid="crew-play-injury">Injury variant</Btn>
                <Btn onClick={() => auditionCrew("recovery")} testid="crew-play-recovery">Recovery variant</Btn>
                <Btn onClick={() => auditionCrew("defining")} testid="crew-play-defining">Defining moment</Btn>
                <Btn onClick={audio.stopCrew} testid="crew-stop"><Stop size={14} weight="fill" /> Stop crew</Btn>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Btn onClick={async () => { await audio.audition("expedition_exploration", exp); auditionCrew(); }} testid="crew-layer">Layer with expedition</Btn>
                <Btn onClick={() => simulateCrewEvents(2)} testid="crew-sim-2">Simulate 2 events</Btn>
                <Btn onClick={() => simulateCrewEvents(5)} testid="crew-sim-max">Test max-3 rule</Btn>
              </div>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-ff-muted">Auditioning never alters real crew records, scoring, or crew performance.</p>
        </Panel>

        <Panel title="Beat Visuals" subtitle="Decorative only — never delays input or affects scoring. Reduced Motion & opt-out disable it." testid="audio-beat-visuals">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Row label="Beat-sync flag">{String(d.flags?.BEAT_SYNC_UI_ENABLED)}</Row>
              <Row label="User beat-sync pref"><span data-testid="bv-pref">{String(!!audio.settings?.beat_sync_enabled)}</span></Row>
              <Row label="Reduced Motion"><span data-testid="bv-reduced-motion">{String(!!b.reducedMotion)}</span></Row>
              <Row label="Visuals active"><span data-testid="bv-active">{String(!!b.active)}</span></Row>
              <Row label="Clock status"><span data-testid="bv-clock">{clk.running ? (clk.paused ? "paused" : "running") : "stopped"}</span></Row>
              <Row label="BPM / signature">{clk.bpm || "—"} · {clk.beatsPerBar || 4}/4</Row>
            </div>
            <div>
              <Row label="Beat / bar"><span data-testid="bv-beat">{clk.beat ?? 0}</span> / {clk.bar ?? 0}</Row>
              <Row label="Drift (ms)"><span data-testid="bv-drift">{clk.drift ?? 0}</span></Row>
              <Row label="Resyncs">{clk.resyncs ?? 0}</Row>
              <Row label="Registered listeners"><span data-testid="bv-listeners">{b.listeners ?? 0}</span></Row>
              <Row label="Active effects">{(b.effects || []).join(", ") || "none"}</Row>
              <Row label="Triggered / suppressed">{b.triggered ?? 0} / {b.suppressed ?? 0}</Row>
              <Row label="Perf suppressed">{String(!!b.perfSuppressed)}</Row>
              <Row label="Visual errors">{b.errors ?? 0}</Row>
              <Row label="Last event">{b.lastEvent?.type || "—"}</Row>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Btn onClick={() => audio.beat.trigger("beat")} testid="bv-trigger-beat">Beat</Btn>
            <Btn onClick={() => audio.beat.trigger("bar_start")} testid="bv-trigger-bar">Bar start</Btn>
            <Btn onClick={() => audio.beat.trigger("phrase_start")} testid="bv-trigger-phrase">Phrase start</Btn>
            <Btn onClick={() => audio.beat.trigger("result_resolve")} testid="bv-trigger-result">Result resolve</Btn>
            <Btn onClick={() => audio.beat.pauseClock()} testid="bv-pause-clock">Pause clock</Btn>
            <Btn onClick={() => audio.beat.resumeClock()} testid="bv-resume-clock">Resume clock</Btn>
            <Btn onClick={() => audio.beat.simulateReducedMotion(!b.reducedMotion)} testid="bv-sim-reduced">Toggle Reduced Motion</Btn>
            <Btn onClick={() => audio.beat.clearLog()} testid="bv-clear-log">Clear log</Btn>
          </div>
          <p className="mt-2 text-[11px] text-ff-muted">These controls are diagnostics only and never affect gameplay records.</p>
        </Panel>

        <p className="text-center text-xs text-ff-muted">Ordinary players and admins never receive audio — this preview requires a backend-issued founder entitlement. No subliminal, compulsive, casino-style, or spending-pressure audio is used.</p>
      </div>
    </FounderShell>
  );
}
